import { useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { updateDateRangeEmissions, updateDateRangeConsumption, updateDateRangeFinancials } from "api";

interface UseTotalDataParams {
    searchFilters: any;
    dateRangeStartDate: any;
    dateRangeEndDate: any;
    dateRangeType: string;
    currentMainTab: string;
    subTypeChart: string;
}

interface UseTotalDataReturn {
    totalData: any[];
    isLoadingTotal: boolean;
    error: any;
}

export const useTotalData = ({
    searchFilters,
    dateRangeStartDate,
    dateRangeEndDate,
    dateRangeType,
    currentMainTab,
    subTypeChart,
}: UseTotalDataParams): UseTotalDataReturn => {
    const [totalData, setTotalData] = useState<any[]>([]);
    const [isLoadingTotal, setIsLoadingTotal] = useState(false);
    const [error, setError] = useState<any>(null);

    const apiFn = currentMainTab === "GHG Emissions"
        ? updateDateRangeEmissions
        : currentMainTab === "Consumption"
            ? updateDateRangeConsumption
            : updateDateRangeFinancials;

    const { mutateAsync: updateTotalDateRange } = useMutation(apiFn);

    const isEmissionsChart = subTypeChart === "location" || subTypeChart === "market";

    useEffect(() => {
        const fetchTotalData = async () => {
            setIsLoadingTotal(true);
            setError(null);

            try {
                await updateTotalDateRange({
                    dateRangeRequest: {
                        ...searchFilters,
                        compareFromDate: dateRangeStartDate.toISOString().substring(0, 10),
                        compareToDate: dateRangeEndDate.toISOString().substring(0, 10),
                        groupBy: dateRangeType
                    },
                    chartType: 'total'
                }, {
                    onSuccess: (response: any) => {
                        const { total } = response.data;
                        const totalDataArray = total ? Object.values(total).flat() : [];
                        const processedTotalData = totalDataArray.map((entry: any) =>
                            isEmissionsChart ? entry[`${subTypeChart}BasedEmission`] : entry.consumption ?? entry.amount ?? 0
                        );
                        setTotalData(processedTotalData);
                    },
                    onError: (err: any) => {
                        setError(err);
                    }
                });
            } catch (err) {
                setError(err);
            } finally {
                setIsLoadingTotal(false);
            }
        };

        fetchTotalData();
    }, [searchFilters, dateRangeStartDate, dateRangeEndDate, dateRangeType, currentMainTab, subTypeChart, updateTotalDateRange, isEmissionsChart]);

    return {
        totalData,
        isLoadingTotal,
        error
    };
}; 